<template>
  <div>
    <!-- Hero -->
    <section class="compare-hero">
      <v-container class="py-12 py-md-16">
        <div class="text-center mb-8">
          <v-chip variant="tonal" color="primary" class="mb-4">Comparison</v-chip>
          <h1 class="compare-h1 mb-4">Song7: A focused alternative to Charts by WorshipTools</h1>
          <p class="text-h6 text-medium-emphasis mx-auto" style="max-width: 820px">
            WorshipTools Charts is a free, cross-platform chord chart app and part of the wider WorshipTools suite, with
            a large built-in song catalog. Song7 is a focused, standalone worship platform: chord charts, setlists,
            transposition, team sync, and presentation in one tool, with a fully localized Danish version. This page is
            honest about where the free option wins.
          </p>
          <p class="text-caption text-medium-emphasis mt-4">
            Last updated June 2026 · Honest comparison · Updated quarterly
          </p>

          <div class="mt-6">
            <a href="#migrate" class="switcher-callout">
              <v-icon icon="$check" size="18" class="mr-1" />
              Already on WorshipTools Charts? See how to switch to Song7
              <v-icon icon="$arrowRight" size="16" class="ml-1" />
            </a>
          </div>
        </div>

        <v-row class="mt-4" justify="center">
          <v-col cols="12" md="5">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <div class="d-flex align-center mb-4">
                <v-icon icon="$musicCircleOutline" color="primary" size="32" class="mr-3" />
                <strong class="text-h6">Song7</strong>
              </div>
              <p class="text-body-2 mb-3">
                Focused, standalone worship platform with chord charts, setlists, transposition, real-time team sync,
                and presentation. Web-first, English and Danish.
              </p>
              <ul class="compare-quick-facts">
                <li><strong>Platforms:</strong> Any browser; installable as a PWA</li>
                <li><strong>Built for:</strong> Teams wanting one focused tool</li>
                <li>
                  <strong>Pricing:</strong> Free for teams up to {{ FREE_PLAN.members }} members; paid plans beyond that
                </li>
              </ul>
              <v-btn
                :to="appPath('spaces')"
                color="primary"
                variant="flat"
                rounded="pill"
                class="mt-4"
                append-icon="$arrowRight"
              >
                Try Song7 free
              </v-btn>
            </v-card>
          </v-col>

          <v-col cols="12" md="5">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <div class="d-flex align-center mb-4">
                <v-icon icon="$musicNote" color="grey" size="32" class="mr-3" />
                <strong class="text-h6">WorshipTools Charts</strong>
              </div>
              <p class="text-body-2 mb-3">
                Free chord chart and sheet music app for mobile and web, part of the WorshipTools suite. Large song
                catalog, real-time sync, and rehearse tools.
              </p>
              <ul class="compare-quick-facts">
                <li><strong>Platforms:</strong> iOS, Android, web</li>
                <li><strong>Built for:</strong> Worship teams, suite-connected</li>
                <li><strong>Pricing:</strong> 100% free, unlimited</li>
              </ul>
              <v-btn
                href="https://www.worshiptools.com/charts"
                target="_blank"
                rel="noopener nofollow"
                variant="outlined"
                rounded="pill"
                class="mt-4"
                append-icon="$arrowRight"
              >
                View WorshipTools Charts
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Quick verdict -->
    <section class="bg-surface-bright">
      <v-container class="py-12 py-md-16">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Quick verdict</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">
          The short answer for the people who just want to know.
        </p>
        <v-row>
          <v-col cols="12" md="6">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <h3 class="text-h6 font-weight-bold mb-3">Choose WorshipTools Charts if&hellip;</h3>
              <ul class="verdict-list">
                <li>Budget is decisive. It's free, with unlimited songs, musicians, and services.</li>
                <li>
                  You want a large built-in catalog via SongSelect, PraiseCharts, and Loop Community, plus direct PDF
                  import.
                </li>
                <li>
                  You want ready-made rehearse mixes from a track store, with on-demand looping and up/down mixes tied
                  into your charts.
                </li>
              </ul>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <CompareHighlightCard>
              <h3 class="text-h6 font-weight-bold mb-3">Choose Song7 if&hellip;</h3>
              <ul class="verdict-list">
                <li>
                  You want a focused, standalone tool rather than the on-ramp to a presentation, scheduling, and music
                  store suite.
                </li>
                <li>You're a Danish or Scandinavian team that wants a fully localized app interface.</li>
                <li>
                  You value being able to export your whole library anytime (ChordPro, text, or JSON) with no lock-in,
                  plus extras like photo-to-chart OCR and a built-in tuner.
                </li>
              </ul>
            </CompareHighlightCard>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- At-a-glance comparison table -->
    <section>
      <v-container class="py-12 py-md-16">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">At-a-glance comparison</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center mx-auto" style="max-width: 720px">
          Feature-by-feature snapshot of how Song7 and WorshipTools Charts line up. WorshipTools wins a lot of these,
          and we say so.
        </p>

        <v-card class="overflow-hidden" elevation="0" border rounded="xl">
          <v-table density="comfortable" class="compare-table">
            <thead>
              <tr>
                <th scope="col">Feature</th>
                <th scope="col" class="text-center">Song7</th>
                <th scope="col" class="text-center">WorshipTools Charts</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="row in matrix" :key="row.feature">
                <tr v-if="row.group" class="compare-table-group">
                  <th scope="rowgroup" colspan="3" class="text-uppercase text-caption font-weight-bold">
                    {{ row.group }}
                  </th>
                </tr>
                <tr>
                  <td class="font-weight-medium">{{ row.feature }}</td>
                  <td class="text-center">
                    <v-icon v-if="row.song7 === true" icon="$check" color="success" aria-label="Yes" />
                    <v-icon v-else-if="row.song7 === false" icon="$close" color="grey" aria-label="No" />
                    <span v-else class="text-caption">{{ row.song7 }}</span>
                  </td>
                  <td class="text-center">
                    <v-icon v-if="row.worshipTools === true" icon="$check" color="success" aria-label="Yes" />
                    <v-icon v-else-if="row.worshipTools === false" icon="$close" color="grey" aria-label="No" />
                    <span v-else class="text-caption">{{ row.worshipTools }}</span>
                  </td>
                </tr>
              </template>
            </tbody>
          </v-table>
        </v-card>
        <p class="text-caption text-medium-emphasis mt-3 text-center">
          Sources: worshiptools.com/charts, worshiptools.com/charts/faq. Verified June 2026.
        </p>

        <div class="text-center mt-8">
          <v-btn
            :to="appPath('spaces')"
            color="primary"
            variant="flat"
            size="large"
            rounded="pill"
            append-icon="$arrowRight"
          >
            Start your free Song7 space
          </v-btn>
          <p class="text-caption text-medium-emphasis mt-2">No credit card required</p>
        </div>
      </v-container>
    </section>

    <!-- What is WorshipTools Charts -->
    <section class="bg-surface-bright">
      <v-container class="py-12 py-md-16">
        <v-row>
          <v-col cols="12" md="6">
            <h2 class="text-h4 font-weight-bold mb-4">What is WorshipTools Charts?</h2>
            <p class="text-body-1 mb-4">
              WorshipTools Charts is a free chord chart and sheet music app for mobile and web. It displays charts in
              several modes (lyrics only, one- or two-column chord charts, and Nashville number charts) with
              transposition, capo handling, and a built-in ChordPro editor.
            </p>
            <p class="text-body-1 mb-4">
              It runs on iOS, Android, and in a web browser, and it syncs in real time so that when a leader changes a
              song or key, the team's devices follow. It imports from Loop Community, SongSelect, PraiseCharts, PDFs,
              and ChordPro files, and ships with a large catalog of songs.
            </p>
            <p class="text-body-1 mb-0">
              Charts is one app in the wider WorshipTools suite, alongside Presenter (presentation), Planning
              (scheduling), and Loop Community's track store. That ecosystem is a strength if you want everything in one
              account, and it's the reason the apps can be free.
            </p>
          </v-col>
          <v-col cols="12" md="6">
            <h2 class="text-h4 font-weight-bold mb-4">What is Song7?</h2>
            <p class="text-body-1 mb-4">
              Song7 is a focused, standalone worship music platform for managing your song library, building setlists,
              transposing on the fly, syncing the team in real time, and running the presentation. One tool, one team
              subscription, in English or Danish.
            </p>
            <p class="text-body-1 mb-4">
              It runs in any modern browser and installs as a Progressive Web App on iOS, Android, macOS, ChromeOS, and
              Windows. Song7 isn't the entry point to a larger suite or a music store. It's a single product that does
              the worship-chart-and-setlist job and stops there.
            </p>
            <p class="text-body-1 mb-0">
              Song7 is a private worship-planning tool. The songs, lyrics, and chord charts you add live only inside
              your own team's Space. Song7 does not ship a public catalogue of copyrighted worship music. You are
              responsible for holding the licenses you need for the content you store and share.
            </p>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Feature deep dive -->
    <section>
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Feature-by-feature deep dive</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">
          Where each product wins, where each one falls short.
        </p>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Price and what "free" means</h3>
          <p class="mb-3">
            <strong>WorshipTools Charts:</strong> Free, with unlimited songs, musicians, and services. There's no paid
            tier to unlock the core chart features. This is a genuine advantage, and for many teams it's the whole
            decision.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Free for teams of up to {{ FREE_PLAN.members }} members, then paid plans (with a
            30-day trial) once your team outgrows that. So Song7 isn't simply the paid option, but WorshipTools Charts
            is free for an unlimited team and library, which Song7's paid tiers don't try to undercut. If free with no
            limits is your only criterion, WorshipTools Charts wins outright.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Platform support</h3>
          <p class="mb-3">
            <strong>WorshipTools Charts:</strong> iOS, Android, and web. Like Song7, it runs on essentially any device
            with a browser, including Windows laptops and Surface tablets. On platform breadth, the two are evenly
            matched.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Any modern browser, installable as a Progressive Web App on iOS, Android, macOS,
            ChromeOS, and Windows. Neither tool locks you to one ecosystem the way an Apple-only app would, so this
            isn't a differentiator between these two.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Song and chord chart management</h3>
          <p class="mb-3">
            <strong>WorshipTools Charts:</strong> Deep here. A built-in ChordPro editor, direct PDF import, Nashville
            number charts, multiple view modes, and a large catalog sourced through SongSelect, PraiseCharts, and Loop
            Community. PDF import and the built-in catalog are real strengths Song7 doesn't match.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Built-in editing of your own charts via ChordPro or plain text with chord brackets,
            plus photo-to-chart OCR: snap a printed sheet and Song7 turns it into an editable, transposable chart, not
            just a stored PDF. For sheet music, a song can hold MusicXML scores (one per instrument if you like)
            rendered as staff notation you can transpose, and PDFs or images can be attached and viewed alongside the
            chart, though they stay stored files rather than editable charts. Song7 doesn't provide a public song
            catalog; you add and license your own content.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">The editor, and getting your data out</h3>
          <p class="mb-3">
            <strong>WorshipTools Charts:</strong> Songs are edited in a built-in ChordPro editor, where chords are
            written inline in square brackets, like [C]Amazing [G]grace. ChordPro is the de-facto standard these tools
            are built on; it's compact and portable, and many people prefer it. We couldn't find a documented export
            feature, though, so in practice getting a song back out means copying it from the editor.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> The editor uses a chords-above-lyrics layout, so you place a chord on its own line
            above the word rather than typing inline brackets. ChordPro is still supported as an import and export
            format, and Song7 can export any song, or your whole library, as ChordPro, plain text, or JSON. Which
            editing style you prefer is genuinely a matter of taste; what isn't a matter of taste is that Song7 lets you
            take your library with you.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Setlists and planning</h3>
          <p class="mb-3">
            <strong>WorshipTools Charts:</strong> Build a set by typing song names and it automatically locates the
            charts and transposes them, then shares the set with the band. The auto-find-and-transpose flow, backed by
            the catalog, is genuinely fast.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Drag-and-drop setlist builder. Pick songs from your library, reorder by dragging,
            set the key per song, publish, and the whole team sees it. Song7 works from your own library rather than a
            catalog, so there's no "type a name and it appears" magic, but you have full control of every chart.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Transposition and real-time sync</h3>
          <p class="mb-3">
            <strong>WorshipTools Charts:</strong> One-tap transposition, capo handling, and real-time sync so a leader's
            key change pushes to the team's devices. This is solid and on par with Song7.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> The same essentials: one-tap transposition into any key, capo support, and real-time
            team sync of plan and chart changes. On this core worship-team workflow, the two products are closely
            matched.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Rehearse tools and backing tracks</h3>
          <p class="mb-3">
            <strong>WorshipTools Charts:</strong> A clear win. An integrated metronome, links to full songs via Spotify,
            Apple Music, and YouTube, and a Rehearse tool with up/down mixes, on-demand looping, and click and cue
            tracks, with mixes available from the Loop Community store. Its looping and store-bought mixes go further
            than Song7.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Each song can carry its own audio (a backing track, a click, a guide vocal, or a
            stem per instrument) uploaded by your team and played back in sync, with mute, solo, and a fader per track
            and a shared team mix. Reference recordings from Spotify, YouTube, and Apple Music attach to the song, and a
            built-in metronome and tuner sit alongside. You supply the tracks: Song7 doesn't sell them, and there's no
            section looping or tempo/pitch change. If you want to buy ready-made mixes and loop sections while you
            practice, WorshipTools Charts is the stronger fit.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Presentation</h3>
          <p class="mb-3">
            <strong>WorshipTools Charts:</strong> Congregation projection isn't in Charts itself. It's handled by
            Presenter, a separate (also free) app in the suite that Charts integrates with for automatic page
            advancement. Two apps, one ecosystem.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Has a congregation-facing presentation mode built into the same tool, with clean,
            full-screen lyrics on any screen with a browser. One product covers both the band's charts and the
            lyrics-projection job, where WorshipTools splits them across Charts and Presenter.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">One focused tool or an ecosystem</h3>
          <p class="mb-3">
            <strong>WorshipTools Charts:</strong> Designed to connect to Presenter, to Planning, and to Loop Community's
            track store, all under one WorshipTools account. If you want a single ecosystem for presentation,
            scheduling, charts, and tracks, that breadth is the point.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> Deliberately narrow. It does charts, setlists, transposition, sync, and presentation
            for a worship team and doesn't try to be a scheduling system or a music marketplace. Everything lives on one
            surface behind a single login. There's no separate app to open for planning and nothing to switch between.
            For teams that want one simple tool to learn rather than a suite to adopt, that focus is the appeal.
          </p>
        </article>

        <article class="deep-dive">
          <h3 class="text-h5 font-weight-bold mb-3">Language and localization</h3>
          <p class="mb-3">
            <strong>WorshipTools Charts:</strong> An English-first product built around a primarily English-language
            worship catalog and ecosystem.
          </p>
          <p class="mb-0">
            <strong>Song7:</strong> The whole app interface is available in Danish, not just English. Switch language
            from the user menu. For Danish and Scandinavian worship teams who want their tooling in their own language,
            that's a concrete reason Song7 fits where an English-only app doesn't.
          </p>
        </article>
      </v-container>
    </section>

    <!-- Product showcase: where Song7 differs -->
    <section>
      <v-container class="py-12 py-md-16">
        <div class="text-center mb-10">
          <p class="text-primary font-weight-bold mb-2">Beyond the comparison</p>
          <h2 class="text-h4 font-weight-bold mb-3">Where Song7 takes a different approach</h2>
          <p class="text-h6 text-medium-emphasis mx-auto" style="max-width: 720px">
            Not "more features than free". These are the places Song7 is a genuinely different fit.
          </p>
        </div>

        <v-row>
          <v-col v-for="card in showcase" :key="card.title" cols="12" sm="6" lg="4">
            <v-card class="pa-6 h-100 showcase-card" elevation="0" border rounded="xl">
              <div class="showcase-icon mb-4">
                <v-icon :icon="card.icon" size="24" />
              </div>
              <h3 class="text-h6 font-weight-bold mb-2">{{ card.title }}</h3>
              <p class="text-body-2 mb-0">{{ card.text }}</p>
            </v-card>
          </v-col>
        </v-row>

        <div class="text-center mt-10">
          <v-btn to="/" color="primary" variant="text" rounded="pill" append-icon="$arrowRight">
            See the full Song7 feature tour
          </v-btn>
        </div>
      </v-container>
    </section>

    <!-- Pricing comparison -->
    <section class="bg-surface-bright">
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Pricing comparison</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">The honest version: one of these is free.</p>

        <v-row>
          <v-col cols="12" md="6">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <h3 class="text-h6 font-weight-bold mb-3">WorshipTools Charts cost</h3>
              <p class="text-body-2 mb-3">Charts is free, with no paid tier required for its core chart features.</p>
              <ul class="price-breakdown">
                <li><strong>Charts app:</strong> $0, with unlimited songs, musicians, and services</li>
                <li><strong>Presenter (projection):</strong> also free in the suite</li>
                <li>
                  <strong>Loop Community tracks:</strong> paid à la carte. Backing tracks and stems are sold separately,
                  which is part of how the free apps are funded
                </li>
              </ul>
              <p class="text-caption mt-3 mb-0">
                Source: worshiptools.com/charts, verified June 2026. Numbers may change.
              </p>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <CompareHighlightCard>
              <h3 class="text-h6 font-weight-bold mb-3">Song7 cost</h3>
              <p class="text-body-2 mb-3">
                Song7 is free for teams of up to {{ FREE_PLAN.members }} members. Larger teams move to paid plans with a
                30-day trial. You're paying for a focused, independent tool, including the Danish localization, not a
                free app subsidized by a track store.
              </p>
              <p class="text-body-2 mb-4">
                Paid tiers are based on what your team needs, with a 30-day trial and no credit card required to start.
              </p>
              <v-btn to="/pricing" color="primary" variant="flat" rounded="pill" append-icon="$arrowRight">
                See Song7 pricing
              </v-btn>
              <p class="text-caption mt-4 mb-0">One focused tool, billed directly.</p>
            </CompareHighlightCard>
          </v-col>
        </v-row>

        <v-card class="mt-8 pa-6" elevation="0" color="warning" variant="tonal" rounded="xl">
          <h3 class="text-h6 font-weight-bold mb-2">WorshipTools Charts is free: the honest trade-off</h3>
          <p class="text-body-2 mb-0">
            We won't pretend otherwise: a free, cross-platform, team-syncing chart app with a built-in catalog is hard
            to beat on value, and for a lot of teams it's the right call. The trade-offs are that you're adopting an
            ecosystem (account, suite, and a track store that backing tracks are sold through) and an English-first
            product. Song7 is free for individuals and paid for teams, and its team plans don't try to undercut
            WorshipTools' free. Choose Song7 for fit (focus, a Danish version, data you can export), not for price.
          </p>
        </v-card>

        <v-alert type="info" variant="tonal" class="mt-4" density="comfortable" icon="$informationOutline">
          <strong>Honest note on the comparison:</strong> If your deciding factor is cost, or you want the largest
          built-in catalog and rehearse tools, WorshipTools Charts is excellent and free, and Song7 isn't the obvious
          win there. Song7's case is narrower and specific: a focused standalone tool, a localized Danish interface,
          photo OCR, and a private library you fully control.
        </v-alert>

        <div class="text-center mt-8">
          <v-btn to="/pricing" color="primary" variant="outlined" size="large" rounded="pill" append-icon="$arrowRight">
            See Song7 pricing in full
          </v-btn>
        </div>
      </v-container>
    </section>

    <!-- Migration -->
    <section id="migrate">
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-4">Migrating from WorshipTools Charts to Song7</h2>
        <p class="text-body-1 mb-4">
          Moving from WorshipTools Charts to Song7 means taking the charts you've created out of the WorshipTools
          ecosystem and into your own Song7 Space. The practical steps:
        </p>
        <ol class="migration-steps">
          <li>
            <strong>Export your own charts from WorshipTools.</strong> Save the songs you created in a portable format
            Song7 accepts: ChordPro or plain text with chord brackets. ChordPro preserves chord positions and parses
            cleanly. Note that catalog songs you accessed through SongSelect, PraiseCharts, or Loop Community are
            licensed through WorshipTools, not yours to export, so you'll re-source those under your own license.
          </li>
          <li>
            <strong>Create a Song7 account and a Space for your church.</strong> Free trial, no credit card required.
          </li>
          <li>
            <strong>Import your songs into Song7.</strong> Paste in ChordPro or text with chord brackets; Song7 parses
            the chord positions and renders the chart. For charts you only hold as a PDF or printed sheet, Song7's
            photo-to-chart capture is the fallback, or attach the PDF to the song as-is, to view alongside the chart.
          </li>
          <li><strong>Invite your team.</strong> Members get instant access in their browser on any device.</li>
          <li>
            <strong>Recreate one upcoming setlist to test the workflow</strong> before fully switching. Run a midweek
            rehearsal on Song7 to surface anything specific to your team's habits.
          </li>
        </ol>
        <v-alert type="info" variant="tonal" class="mt-6" density="comfortable" icon="$informationOutline">
          <strong>On licensing:</strong> Song7 stores the worship materials you add privately within your team's Space.
          You're responsible for holding the CCLI, SongSelect, publisher, or other permissions required for the content
          you store, copy, or share. Only import or export songs you're licensed or permitted to use, including when
          moving content out of another platform's catalog.
        </v-alert>
        <p class="text-body-1 mt-4 mb-0">
          If WorshipTools' free catalog, rehearse tools, or wider suite are what you value most, Charts is doing the
          right job and there's no reason to switch. Song7 earns its place for teams that want a focused, localized tool
          they fully control.
        </p>
      </v-container>
    </section>

    <!-- Decision framework -->
    <section class="bg-surface-bright">
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Which one should you pick?</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">A short decision guide based on your situation.</p>

        <v-row>
          <v-col cols="12" md="6">
            <v-card class="pa-6 h-100" elevation="0" border rounded="xl">
              <h3 class="text-h6 font-weight-bold mb-3">Stay with (or pick) WorshipTools Charts if</h3>
              <ul class="verdict-list">
                <li>Free is the deciding factor.</li>
                <li>You rely on its built-in catalog and SongSelect / PraiseCharts / Loop Community integrations.</li>
                <li>You already use Presenter or Planning and want Charts in the same ecosystem.</li>
                <li>
                  Store-bought rehearse mixes with section looping, PDF charts, or Nashville number charts are core to
                  your team.
                </li>
              </ul>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <CompareHighlightCard>
              <h3 class="text-h6 font-weight-bold mb-3">Switch to (or pick) Song7 if</h3>
              <ul class="verdict-list">
                <li>You want one focused tool, not a multi-app ecosystem with a music store.</li>
                <li>You're a Danish or Scandinavian church that wants a localized interface.</li>
                <li>Photo-to-chart OCR and a built-in tuner matter to your workflow.</li>
                <li>You'd rather keep a private library you fully control, with no catalog upsell.</li>
                <li>
                  You want chords-above-lyrics editing instead of inline ChordPro brackets, and one-click export so your
                  library is never locked in.
                </li>
              </ul>
            </CompareHighlightCard>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Social proof -->
    <section class="bg-surface-bright">
      <ClientOnly>
        <FeaturedFeedback class="py-8 py-md-12" />
      </ClientOnly>
    </section>

    <!-- FAQ -->
    <section>
      <v-container class="py-12 py-md-16" style="max-width: 920px">
        <h2 class="text-h4 font-weight-bold mb-2 text-center">Frequently asked questions</h2>
        <p class="text-h6 text-medium-emphasis mb-10 text-center">
          The questions worship leaders actually ask before switching.
        </p>

        <FaqAccordion :groups="[{ items: faqItems }]" />
      </v-container>
    </section>

    <!-- Was this comparison helpful -->
    <section>
      <v-container class="py-4" style="max-width: 720px">
        <PageFeedback title="WorshipTools Charts" context="Comparison" question="Was this comparison helpful?" />
      </v-container>
    </section>

    <!-- Final CTA -->
    <section>
      <v-container class="py-12 py-md-16 text-center" style="max-width: 720px">
        <h2 class="text-h4 font-weight-bold mb-4">Ready to try Song7?</h2>
        <p class="text-h6 text-medium-emphasis mb-6">
          Free trial, no credit card required. Bring your team across whenever you're ready.
        </p>
        <v-btn
          :to="appPath('spaces')"
          color="primary"
          variant="flat"
          size="x-large"
          rounded="pill"
          append-icon="$arrowRight"
          class="pulse-button"
        >
          Start free
        </v-btn>
        <p class="text-caption text-medium-emphasis mt-4">
          Questions? <NuxtLink to="/contact">Talk to us first</NuxtLink>.
        </p>
      </v-container>
    </section>

    <!-- Related comparisons -->
    <CompareRelated current-slug="worshiptools-charts" />
  </div>
</template>

<script setup>
import { useSeo } from '~/composables/useSeo'
import { FREE_PLAN } from '@@/constants/publicPlans'

definePageMeta({
  layout: 'website',
})

const showcase = [
  {
    icon: '$camera',
    title: 'Photo-to-chart OCR import',
    text: 'Snap a picture of a printed chord sheet and Song7 turns it into an editable, transposable chart. WorshipTools imports PDFs, but they stay as stored files rather than editable charts.',
  },
  {
    icon: '$tuning',
    title: 'Built-in tuner',
    text: 'Song7 ships a guitar tuner alongside the metronome, so musicians can tune up in the same tool they read from.',
  },
  {
    icon: '$earth',
    title: 'Fully localized in Danish',
    text: "The entire Song7 app interface runs in Danish, not just English. For Danish and Scandinavian teams, that's a first-class experience, not a translation afterthought.",
  },
  {
    icon: '$heartOutline',
    title: 'One surface, one login',
    text: 'Charts, setlists, transposition, sync, and presentation all live in one place behind a single login. No separate app to open for planning, nothing to switch between.',
  },
  {
    icon: '$download',
    title: 'Export anytime, no lock-in',
    text: 'Download any song, or your whole library, as ChordPro, plain text, or JSON. WorshipTools Charts documents importing songs but not a way to export them back out.',
  },
  {
    icon: '$domain',
    title: 'Multi-Space for multi-campus churches',
    text: 'Run separate song libraries, setlists, and teams per campus or worship team, all under one account.',
  },
]

const matrix = [
  // Product structure
  {
    group: 'Product structure',
    feature: 'Standalone product (no other subscription required)',
    song7: true,
    worshipTools: true,
  },
  {
    feature: 'Free for unlimited songs, musicians & services',
    song7: `Up to ${FREE_PLAN.members} members`,
    worshipTools: true,
  },
  { feature: 'Independent of a wider suite / ecosystem', song7: true, worshipTools: 'Part of WorshipTools suite' },
  { feature: 'Built-in catalog of worship songs', song7: false, worshipTools: true },
  { feature: 'Fully localized Danish version', song7: true, worshipTools: 'Not documented' },

  // Platforms
  { group: 'Platforms', feature: 'Runs in any browser (web app)', song7: true, worshipTools: true },
  { feature: 'iOS (iPhone & iPad)', song7: true, worshipTools: true },
  { feature: 'Android phone & tablet', song7: true, worshipTools: true },
  { feature: 'Windows / macOS via browser', song7: true, worshipTools: true },
  { feature: 'Installable as PWA (home-screen)', song7: true, worshipTools: 'Not documented' },

  // Song & chart management
  { group: 'Song & chart management', feature: 'Your own private song library', song7: true, worshipTools: true },
  { feature: 'Built-in ChordPro editor / import', song7: true, worshipTools: true },
  { feature: 'Plain-text lyrics + chord brackets import', song7: true, worshipTools: true },
  { feature: 'Chords-above-lyrics editing (no inline brackets required)', song7: true, worshipTools: false },
  { feature: 'PDF import', song7: 'Attach & view', worshipTools: true },
  { feature: 'MusicXML notation (rendered, transposable)', song7: true, worshipTools: 'Not documented' },
  { feature: 'Photo OCR to an editable chart', song7: true, worshipTools: false },
  { feature: 'Export songs (ChordPro, text, JSON)', song7: true, worshipTools: 'Not documented' },
  { feature: 'Capo support', song7: true, worshipTools: true },
  { feature: 'Nashville number charts', song7: false, worshipTools: true },
  { feature: 'Multiple view modes (lyrics / 1-col / 2-col)', song7: 'Chart + lyrics', worshipTools: true },
  {
    feature: 'Integrations with licensed sources (SongSelect, PraiseCharts, Loop Community)',
    song7: false,
    worshipTools: true,
  },

  // Setlists & planning
  { group: 'Setlists & planning', feature: 'Drag-and-drop setlist builder', song7: true, worshipTools: true },
  { feature: 'Auto-find & auto-transpose songs into a set', song7: 'Manual', worshipTools: true },
  { feature: 'Per-song key in setlist', song7: true, worshipTools: true },
  { feature: 'Share setlists with the band', song7: true, worshipTools: true },
  { feature: 'Set history (see what was played before)', song7: true, worshipTools: 'Not documented' },

  // Transposition
  { group: 'Transposition', feature: 'One-tap transposition into any key', song7: true, worshipTools: true },
  { feature: 'Key change syncs to the team in real time', song7: true, worshipTools: true },

  // Live use
  {
    group: 'Live use & performance',
    feature: 'Real-time team sync of song & key changes',
    song7: true,
    worshipTools: true,
  },
  { feature: 'Built-in metronome', song7: true, worshipTools: true },
  { feature: 'Built-in tuner', song7: true, worshipTools: 'Not documented' },
  { feature: 'Backing tracks & stems in sync, per-track mix', song7: true, worshipTools: true },
  { feature: 'Section looping in audio playback', song7: false, worshipTools: true },
  { feature: 'Streaming links (Spotify, Apple Music, YouTube)', song7: true, worshipTools: true },
  { feature: 'Annotations / markup on charts', song7: 'Notes only', worshipTools: true },
  { feature: 'Foot-pedal page turn (hardware)', song7: false, worshipTools: 'Not documented' },
  { feature: 'Congregation lyrics projection in the same app', song7: true, worshipTools: 'Via Presenter app' },

  // Team
  {
    group: 'Team & collaboration',
    feature: 'Shared library with real-time sync',
    song7: true,
    worshipTools: true,
  },
  { feature: 'Multi-team / multi-campus separation', song7: 'Multi-Space', worshipTools: 'Not documented' },
  { feature: 'Free for unlimited team members', song7: false, worshipTools: true },
  { feature: 'Notes on songs', song7: 'Shared with the team', worshipTools: true },
]

const faqItems = [
  {
    question: 'Is WorshipTools Charts really free?',
    answer:
      'Yes. WorshipTools describes Charts as "100% free," with unlimited songs, musicians, and services, and no paid tier required for its core chart features. The wider WorshipTools ecosystem is funded in part by Loop Community, which sells backing tracks and stems separately. For a team whose deciding factor is cost, Charts being free is a genuine and significant advantage.',
  },
  {
    question: "What's the difference between Song7 and WorshipTools Charts?",
    answer:
      'WorshipTools Charts is a free chord chart app and part of a wider suite (Presenter, Planning, and the Loop Community track store), with a large built-in catalog, PDF import, Nashville charts, and rehearse tools. Song7 is a focused, standalone tool (free for individuals, paid for teams) that does charts, setlists, transposition, team sync, and presentation in one app, with a fully localized Danish version. They overlap heavily on the core worship-team workflow; the differences are price, ecosystem, catalog, rehearse tools, and localization.',
  },
  {
    question: 'Why would I pay for Song7 when WorshipTools Charts is free?',
    answer:
      "Honestly, for many teams you wouldn't. If price or the built-in catalog is your deciding factor, WorshipTools Charts is the better call and we'll say so. Song7's case is specific: you want a focused, independent tool rather than an ecosystem with a music store; you want a fully localized Danish interface; or you value features like photo-to-chart OCR, a built-in tuner, and a private library with no catalog upsell. If none of those matter to you, free is hard to beat.",
  },
  {
    question: 'Does Song7 work on Android and Windows like WorshipTools Charts?',
    answer:
      'Yes. Song7 runs in any browser on Windows, Android, macOS, ChromeOS, and iOS, and installs as a Progressive Web App. On platform breadth the two are evenly matched: both are cross-platform web-and-mobile apps, so this is not a differentiator between them.',
  },
  {
    question: 'Does Song7 have a Danish version?',
    answer:
      'Yes. The whole Song7 app interface is available in Danish. Switch language from the user menu. WorshipTools Charts is an English-first product, so for Danish and Scandinavian worship teams who want their tooling in their own language, Song7 offers a first-class localized experience.',
  },
  {
    question: 'Can I import my songs from WorshipTools Charts into Song7?',
    answer:
      "You can import the charts you created yourself. Save them as ChordPro or plain text with chord brackets and Song7 parses them cleanly; for charts you only hold as a PDF or printed sheet, Song7's photo-to-chart capture is the fallback. Note that catalog songs you accessed through SongSelect, PraiseCharts, or Loop Community are licensed through WorshipTools, not yours to export, so you'd re-source those under your own license. A PDF can also be attached to a song and viewed as-is.",
  },
  {
    question: 'Does Song7 import PDFs?',
    answer:
      "Not as a chart. You can attach a PDF (or an image) to a song and view it alongside the chart, but Song7 doesn't turn it into an editable, transposable chart. For that it takes ChordPro, plain text with chord brackets, or a photo through photo-to-chart OCR. WorshipTools Charts reads PDFs as charts directly, which is a real advantage if your charts are PDF-based. For sheet music, Song7 also renders MusicXML as transposable staff notation.",
  },
  {
    question: 'Do I have to write chords in ChordPro brackets in Song7?',
    answer:
      "No. Song7's editor uses a chords-above-lyrics layout, so you place chords on their own line above the words, rather than typing inline [C] brackets the way a ChordPro editor like the one in WorshipTools Charts does. ChordPro is still fully supported in Song7 as both an import and an export format, so you can bring bracket-style charts in and send them back out, but you aren't required to write in that syntax. WorshipTools Charts is built around a ChordPro editor, which many people prefer. It's a genuine matter of taste.",
  },
  {
    question: 'Can I export my songs out of WorshipTools Charts, or Song7?',
    answer:
      "Song7 exports any song, or your whole library, as ChordPro, plain text, or JSON, so you're never locked in. For WorshipTools Charts we couldn't find a documented export feature. It documents importing songs (PDF and ChordPro) but not a way to download them back out, so in practice getting a chart out means copying it from the editor. If keeping your data portable matters to you, that's a point in Song7's favor.",
  },
  {
    question: 'Does Song7 have backing tracks or rehearse tools like WorshipTools?',
    answer:
      'Yes, for the tracks you bring. A Song7 song can hold its own audio (backing track, click, guide vocal, or a stem per instrument) that plays in sync, with mute, solo, and a fader per track and a team mix everyone starts from. Reference recordings from Spotify, YouTube, and Apple Music attach to the song too. The difference: WorshipTools sells ready-made mixes through Loop Community and adds on-demand looping; Song7 plays the tracks your team uploads and has no section looping or tempo/pitch change.',
  },
  {
    question: 'Does Song7 give me a database of worship songs to use?',
    answer:
      "No. Song7 is a private worship-planning tool, not a public catalogue of copyrighted music. Unlike WorshipTools, which provides catalog access through licensed integrations, Song7 holds only the songs you add, stored privately inside your own team's Space. You are responsible for holding the CCLI, SongSelect, publisher, or other licenses required for the content you store, copy, or share.",
  },
  {
    question: 'Does Song7 do congregation projection?',
    answer:
      'Yes, in the same app. Song7 has a congregation-facing presentation mode that displays full-screen lyrics on any screen with a browser. WorshipTools handles projection in Presenter, a separate (also free) app in its suite that Charts integrates with, so WorshipTools covers projection too, just across two apps rather than one.',
  },
  {
    question: "What's the best alternative to WorshipTools Charts?",
    answer:
      "It depends on what you need. If you want free with a large catalog and rehearse tools, there isn't an obvious reason to leave WorshipTools Charts. If you want a focused standalone tool, a localized Danish interface, or features like photo-to-chart OCR, Song7 is a fit. Other tools worth comparing include OnSong (Apple-only, individual-first) and Planning Center Music Stand (tied to Planning Center Services).",
  },
]

useSeo({
  keywords: [
    'worshiptools charts alternative',
    'charts by worshiptools alternative',
    'best worshiptools alternative',
    'worshiptools charts review',
    'song7 vs worshiptools charts',
    'worshiptools charts vs song7',
    'free worship chord chart app alternative',
    'worship chord chart app',
  ],
  schema: 'SoftwareApplication',
  faq: faqItems,
})
</script>

<style scoped>
.compare-hero {
  background:
    radial-gradient(1000px 600px at 80% 0%, rgba(var(--v-theme-primary), 0.12), transparent 60%),
    radial-gradient(800px 500px at 0% 30%, rgba(var(--v-theme-secondary), 0.08), transparent 60%);
}

.compare-h1 {
  font-size: clamp(34px, 5vw, 56px);
  font-weight: 800;
  letter-spacing: -0.025em;
  line-height: 1.1;
}

.compare-quick-facts {
  list-style: none;
  padding: 0;
  margin: 0;
}

.compare-quick-facts li {
  padding: 4px 0;
  font-size: 0.95rem;
}

.verdict-list,
.migration-steps {
  padding-left: 1.25rem;
  margin: 0;
}

.verdict-list li,
.migration-steps li {
  margin-bottom: 0.6rem;
  line-height: 1.55;
}

.compare-table th {
  font-weight: 700 !important;
  font-size: 0.95rem;
}

.deep-dive {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  padding: 1.75rem 0;
}

.deep-dive:last-child {
  border-bottom: none;
}

.deep-dive p {
  line-height: 1.65;
}

.price-breakdown {
  list-style: none;
  padding: 0;
  margin: 0;
}

.price-breakdown li {
  padding: 8px 0;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  font-size: 0.95rem;
}

.price-breakdown li:last-child {
  border-bottom: none;
}

.pulse-button {
  animation: pulseButton 2s infinite;
}

.switcher-callout {
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  background: rgba(var(--v-theme-success), 0.1);
  color: rgb(var(--v-theme-success));
  border-radius: 999px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: background 0.2s ease;
}

.switcher-callout:hover {
  background: rgba(var(--v-theme-success), 0.18);
}

.compare-table-group th {
  background: rgba(var(--v-theme-on-surface), 0.04);
  color: rgba(var(--v-theme-on-surface), 0.6);
  letter-spacing: 0.06em;
  padding-top: 16px !important;
  padding-bottom: 8px !important;
}

.showcase-card {
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.showcase-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 18px 40px rgba(15, 23, 42, 0.06);
}

.showcase-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-primary), 0.12);
  color: rgb(var(--v-theme-primary));
}

@keyframes pulseButton {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0.4);
  }
  70% {
    box-shadow: 0 0 0 15px rgba(var(--v-theme-primary), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--v-theme-primary), 0);
  }
}
</style>
